import { useState } from 'react';

interface CloneTournamentModalProps {
  sourceName: string;
  defaultStartDate: string;
  defaultEndDate: string;
  onConfirm: (name: string, startDate: string, endDate: string) => Promise<void>;
  onCancel: () => void;
}

export default function CloneTournamentModal({
  sourceName,
  defaultStartDate,
  defaultEndDate,
  onConfirm,
  onCancel,
}: CloneTournamentModalProps) {
  const [name, setName] = useState(`Copy of ${sourceName}`);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setError('Tournament name is required.'); return; }
    if (!startDate || !endDate) { setError('Start and end dates are required.'); return; }
    if (endDate < startDate) { setError('End date must be on or after start date.'); return; }
    setSaving(true);
    setError(null);
    try {
      await onConfirm(trimmed, startDate, endDate);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Clone failed. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="px-5 pt-5 pb-4 border-b border-border">
          <h2 className="text-base font-bold text-text-primary">Clone Tournament</h2>
          <p className="text-xs text-text-muted mt-0.5">
            Creates a full copy of <span className="font-semibold text-text-secondary">{sourceName}</span> — all matches, sets and rallies included.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">New Tournament Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="input-field text-sm"
              placeholder="e.g. Summer League 2027"
              required
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1.5">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="input-field text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1.5">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="input-field text-sm"
                required
              />
            </div>
          </div>

          {error && (
            <p className="text-xs text-error font-semibold">{error}</p>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="flex-1 py-3 rounded-xl font-semibold text-sm border border-border text-text-secondary hover:bg-surface-raised transition-all active:scale-[0.98] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 rounded-xl font-bold text-sm bg-info text-white hover:opacity-90 transition-all active:scale-[0.98] disabled:opacity-50"
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Cloning…
                </span>
              ) : 'Clone'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
